import { CommonModule } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Component, OnInit, signal, ViewChild, WritableSignal } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BadgeModule } from 'primeng/badge';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { Popover, PopoverModule } from 'primeng/popover';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';

import { ColumnConfig } from '../../../../shared/components/table-header/header-config.model';
import { TableHeaderComponent } from '../../../../shared/components/table-header/table-header.component';
import { PaginationResponse } from '../../../../shared/models/pagination-response.model';
import { TruncatePipe } from '../../../../shared/pipes/truncate.pipe';
import { ServiceRequest } from '../../service-request.model';
import { ServiceRequestService } from '../../service-request.service';
import { ServiceRequestListFilterComponent } from './service-request-list-filter/service-request-list-filter.component';

@Component({
  selector: 'carehub-service-request-list',
  imports: [
    CommonModule,
    BreadcrumbModule,
    TableModule,
    BadgeModule,
    ButtonModule,
    CardModule,
    PopoverModule,
    SelectModule,
    PaginatorModule,
    RouterModule,
    TagModule,
    TruncatePipe,
    TooltipModule,
    TableHeaderComponent,
    ServiceRequestListFilterComponent,
  ],
  templateUrl: './service-request-list.component.html',
  styleUrl: './service-request-list.component.scss',
})
export class ServiceRequestListComponent implements OnInit {
  breadcrumbHome: MenuItem = {
    routerLink: '/',
  };

  breadcrumbItems: MenuItem[] = [
    {
      label: $localize`:@@sr_list.breadcrumb.root:Service Requests`,
      routerLink: '/utilities/service-requests',
    },
  ];

  serviceRequests!: PaginationResponse<ServiceRequest[]>;
  showEditServiceRequest: WritableSignal<boolean> = signal(false);
  selectedServiceRequest?: WritableSignal<ServiceRequest>;
  @ViewChild('op') op!: Popover;

  actionOptions: string[] = ['Pick action', 'Edit', 'Chat'];

  searchParams!: HttpParams;
  sortingParams!: string[];

  headerConfig: ColumnConfig[] = [
    { header: 'Id', sortable: true, field: 'Id', sort: 'desc' },
    { header: $localize`:@@sr_list.table.header.title:Title`, sortable: false },
    { header: $localize`:@@sr_list.table.header.status:Status`, sortable: false },
    { header: $localize`:@@sr_list.table.header.created_by:Created by`, sortable: false },
    { header: $localize`:@@sr_list.table.header.assigned_to:Assigned to`, sortable: false },
    { header: $localize`:@@sr_list.table.header.priority:Priority`, sortable: false },
    {
      header: $localize`:@@sr_list.table.header.created_date:Created date`,
      sortable: false,
    },
    { header: '', sortable: false },
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private _serviceRequestService: ServiceRequestService,
    private _messageService: MessageService,
    private activatedRoute: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.serviceRequests = this.route.snapshot.data['serviceRequests'];
    // If filter string is present in the URL query params, set it to the filter string
    if (!this.route.snapshot.queryParams['filter']) {
      this.searchParams = new HttpParams();

      this.searchParams = this.searchParams.append('status', 'New');
      this.searchParams = this.searchParams.append('status', 'Paused');
      this.searchParams = this.searchParams.append('status', 'Approved');
      this.searchParams = this.searchParams.append('status', 'Contested');
      this.searchParams = this.searchParams.append('status', 'Awaiting user');
      this.searchParams = this.searchParams.append('status', 'In progress');
      this.searchParams = this.searchParams.append('status', 'Committed');
      this.searchParams = this.searchParams.append('status', 'Blocked/Awaiting');
      this.searchParams = this.searchParams.append('sort', 'Id:desc');
    } else {
      this.searchParams = new HttpParams({
        fromString: this.route.snapshot.queryParams['filter'],
      });
      this.setSorting();
    }
  }

  /**
   * Formats a status label by converting it to lowercase and replacing spaces or slashes with hyphens.
   *
   * @param status - The status string to format.
   * @returns The formatted status label as a lowercase, hyphen-separated string.
   *
   * For example, "In Progress/Completed" becomes "in-progress-completed".
   */
  formatStatusLabel(status: string): string {
    return status.toLowerCase().replace(/\s|\/+/g, '-');
  }

  /**
   * Set the filters and sorting for the service requests
   */
  setSorting(): void {
    // Set sorting parameters for the visualization
    const sortParams: string[] = this.searchParams.getAll('sort') || [];

    this.headerConfig.forEach((header: ColumnConfig) => {
      // Check if the header is in the sortParams
      const sortParam: string | undefined = sortParams.find((param: string) =>
        param.startsWith(header.field ? header.field : '')
      );
      if (sortParam && header.sortable) {
        // If it is, set the sort to asc or desc based on the param
        const order: string[] = sortParam.split(':');
        header.sort = order[1] as 'asc' | 'desc';
      }
    });

    this.sortingParams = sortParams;
  }

  /**
   * Update the sorting of the service requests
   *
   * @param sortingArray string[]
   */
  async updateSorting(sortingArray: string[]): Promise<void> {
    this.sortingParams = sortingArray;
    this.setSortingParams(sortingArray);

    if (sortingArray.length > 0) {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: {
          filter: this.searchParams.toString(),
        },
        replaceUrl: true,
      });
    } else {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        replaceUrl: true,
      });
    }

    this.serviceRequests = await this._serviceRequestService.get(1, this.searchParams);
  }

  /**
   * Set the sorting parameters for the service requests
   *
   * @param sortingArray string[]
   */
  setSortingParams(sortingArray: string[]): void {
    this.searchParams = this.searchParams.delete('sort');

    if (sortingArray.length > 0) {
      sortingArray.forEach((param: string) => {
        const [key, value] = param.split(':');
        this.searchParams = this.searchParams.set('sort', `${key}:${value}`);
      });
    }
  }

  /**
   * Get the service requests based on the filter string
   *
   * @param filterString string
   */
  async updateFilters(incoming: HttpParams): Promise<void> {
    const existingSort: string[] = this.searchParams?.getAll('sort') ?? [];
    let next: HttpParams = incoming;

    if (existingSort.length && !next.has('sort')) {
      for (const sort of existingSort) {
        next = next.append('sort', sort);
      }
    }

    this.searchParams = next;
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { filter: this.searchParams.toString() },
      replaceUrl: true,
    });

    this.serviceRequests = await this._serviceRequestService.get(1, this.searchParams);
  }

  /**
   * Change the page of the service requests list
   *
   * @param paginatorState
   */
  async changePage(paginatorState: PaginatorState): Promise<void> {
    this.serviceRequests = await this._serviceRequestService.get(
      paginatorState.page ? paginatorState.page + 1 : 1,
      this.searchParams
    );
  }

  /**
   * Start a teams chat regarding the service request
   *
   * @param servicerequest ServiceRequest
   */
  startTeamsChat(serviceRequest: ServiceRequest): void {
    // If the service request is not assigned to anyone, show an warning message.
    if (serviceRequest.assignedTo === 'Unassigned') {
      this._messageService.add({
        severity: 'warn',
        summary: $localize`:@@sr_list.message.warning:Warning`,
        detail: $localize`:@@sr_list.message.unassigned_ticket:The ticket has not been assigned to anyone yet, so you can not chat with someone about this ticket.`,
        life: 5000,
      });
      return;
    }
    this._serviceRequestService.joinTeamsChat(serviceRequest);
  }
}
